import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { customerNotificationsTourId, customerNotificationsSteps } from '@/tours/customer';
import { BellOff, CheckCheck } from 'lucide-react';
import { notificationsApi } from '@/services/api/notificationsApi';
import { queryKeys } from '@/services/api/queryClient';
import { EmptyState, LoadingSpinner, ErrorMessage, Pagination, Button } from '@/components/ui';
import NotificationItem from '@/components/features/customer/NotificationItem';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const ITEMS_PER_PAGE = 20;

const NotificationsPage: React.FC = () => {
  useTourAutoStart({ tourId: customerNotificationsTourId, steps: customerNotificationsSteps });
  const [page, setPage] = useState(1);
  const qc = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: queryKeys.notificationList(String(page)),
    queryFn: () => notificationsApi.getList(page, ITEMS_PER_PAGE),
  });

  const markReadMutation = useMutation({
    mutationFn: notificationsApi.markRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications });
      qc.invalidateQueries({ queryKey: queryKeys.unreadCount });
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Не удалось отметить уведомление'));
    },
  });

  const notifications = data?.data ?? [];
  const totalPages = data ? Math.ceil(data.count / ITEMS_PER_PAGE) : 1;
  const hasUnread = notifications.some(n => !n.is_read);

  const handleMarkAllRead = () => {
    const unread = notifications.filter(n => !n.is_read);
    unread.forEach(n => markReadMutation.mutate(n.id));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Уведомления</h1>
          <p className="text-sm text-gray-500 mt-1">Статусы и обновления по заказам</p>
        </div>

        {hasUnread && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMarkAllRead}
            loading={markReadMutation.isPending}
          >
            <CheckCheck className="w-4 h-4" />
            Прочитать все
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="py-20">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {isError && (
        <ErrorMessage
          message="Не удалось загрузить уведомления"
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && notifications.length === 0 && (
        <EmptyState
          icon={<BellOff className="w-8 h-8" />}
          title="Нет уведомлений"
          description="Здесь будут появляться обновления по вашим заказам"
        />
      )}

      {!isLoading && !isError && notifications.length > 0 && (
        <>
          <div data-tour="customer:notifications-list" className="space-y-2">
            {notifications.map(n => (
              <NotificationItem
                key={n.id}
                notification={n}
                onMarkRead={(id) => markReadMutation.mutate(id)}
              />
            ))}
          </div>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            className="mt-6"
          />
        </>
      )}
    </div>
  );
};

export default NotificationsPage;
